/**
 * F7-Range — the tier reveal. Classifies the job and invites the customer into
 * the free estimate. No price anywhere — the customer sees a plain-language
 * scope preview, never a number.
 *
 * The scope comes from a customer estimate created against a rough answer set
 * (items only — price-free, enforced server-side).
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCustomerEstimate, type AnswerSetPayload } from '../lib/api';
import type { CustomerEstimate } from '../lib/types';
import { ScopeList } from '../components/ScopeList';

interface RangeState {
  serviceLine?: string;
  productCode?: string;
}

// A minimal rough answer set for the stage-zero scope preview. The real intake
// builds this from F3/F4; here we send the chosen product's baseline triggers.
function roughAnswerSet(state: RangeState): AnswerSetPayload {
  return {
    service_line: state.serviceLine ?? 'BATHROOM',
    product_code: state.productCode ?? 'BATH_FULL',
    triggers: [],
    activeModifierCodes: [],
    unsureQuestionCodes: [],
    unsureOptions: {},
    sessionVars: {},
  };
}

export function Range(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as RangeState) ?? {};
  const [estimate, setEstimate] = useState<CustomerEstimate | null>(null);

  useEffect(() => {
    let active = true;
    createCustomerEstimate(roughAnswerSet(state))
      .then((e) => {
        if (active) setEstimate(e);
      })
      .catch(() => {
        /* A missing preview is fine — the intro stands on its own. */
      });
    return () => {
      active = false;
    };
  }, [state]);

  const tierLabel =
    state.productCode === 'BATH_GUT'
      ? 'Looks like a layout change'
      : state.productCode === 'BATH_REFRESH'
        ? 'Looks like a refresh'
        : 'Looks like a full rebuild, same layout';

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <div className="progress__count">{tierLabel.toUpperCase()}</div>
        <h1>Here&rsquo;s the shape of your project</h1>

        <p className="stage-card__lede">
          Answer a handful of questions and we&rsquo;ll put together a real scope for the work
          &mdash; the pieces that matter, in plain language. It takes about fifteen minutes, and
          your estimate is free.
        </p>

        {estimate && estimate.items.length > 0 ? (
          <ScopeList variant="hero" items={estimate.items} />
        ) : null}

        <div className="q-actions" style={{ flexDirection: 'column' }}>
          <button
            type="button"
            className="pb-btn pb-btn--lg"
            onClick={() => navigate('/design/welcome', { state })}
          >
            Start your free estimate
          </button>
          <button type="button" className="pb-btn pb-btn--ghost pb-btn--lg" onClick={() => navigate('/talk')}>
            Talk it through first
          </button>
        </div>
        <p className="estimate__hint" style={{ textAlign: 'center' }}>
          Not the right time? This stays saved under your email.
        </p>
      </div>
    </div>
  );
}
